import React, { useEffect, useState } from 'react';
import {
  Form, Button, Spin, Alert, message, Tag,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useGetDealQuery,
  useUpdateDealMutation,
  useListMerchantsQuery,
  useListCategoriesQuery,
} from '../features/deals/dealsApi';
import DealForm, { dealToFormValues, formValuesToPayload } from '../features/deals/DealForm';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';
import { adminApi } from '../services/adminApi';

const STATUS_COLORS = {
  draft: 'default',
  pending_review: 'orange',
  published: 'green',
  rejected: 'red',
  expired: 'default',
  archived: 'default',
  removed: 'red',
};

export default function AdminDealEditPage() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState(null);

  const { data: deal, isLoading, error } = useGetDealQuery(publicId);
  const { data: merchantsData } = useListMerchantsQuery();
  const { data: categoriesData } = useListCategoriesQuery();
  const [updateDeal, { isLoading: saving }] = useUpdateDealMutation();

  useEffect(() => {
    if (!deal) return;
    form.setFieldsValue(dealToFormValues(deal));
    const cover = deal.images?.[0]?.url || deal.primaryImageUrl || null;
    setImageUrl(cover);
  }, [deal, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = formValuesToPayload(values, imageUrl, { clearImagesWhenEmpty: true });
      await updateDeal({ publicId, ...payload }).unwrap();
      dispatch(adminApi.util.invalidateTags(['Deals', 'Analytics']));
      message.success('Deal updated');
      navigate('/admin/deals');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.data?.title || 'Failed to update deal');
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <Alert
        type="error"
        showIcon
        message="Deal not found"
        action={<Button onClick={() => navigate('/admin/deals')}>Back to deals</Button>}
      />
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Edit Deal"
        subtitle={deal.title}
        extra={(
          <Tag color={STATUS_COLORS[deal.status]}>
            {deal.status?.replace('_', ' ')}
          </Tag>
        )}
      />

      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/admin/deals')}
        style={{ marginBottom: 16, padding: '0 4px' }}
      >
        Back to deals
      </Button>

      <div style={{ ...adminPanelStyle, padding: 24 }}>
        <DealForm
          form={form}
          merchants={merchantsData?.items || []}
          categories={categoriesData?.items || []}
          initialImageUrl={imageUrl}
          onImageChange={setImageUrl}
          compact
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <Button onClick={() => navigate('/admin/deals')}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
